import { useCallback, useEffect, useRef, useState } from 'react';
import { auth } from '../config/firebase';
import { useRegisterNotificationDeviceMutation } from '../api/use-register-notification-device-mutation';
import { useUnregisterNotificationDeviceMutation } from '../api/use-unregister-notification-device-mutation';
import {
  configureFcmDeviceApi,
  enableWebPushNotifications,
  FCM_BLOCK_REASON,
  getFcmBlockReasonMessage,
  getNotificationPermission,
  withFcmOperationTimeout,
  getStoredFcmWebToken,
  getWebPushAvailability,
  handleWebPushPermissionRevoked,
  isPushOptInRequired,
  refreshFcmWebRegistrationIfNeeded,
  resetFcmDeviceApi,
  setupFcmWebRegistration,
  startNotificationPermissionWatcher,
  warmUpFcmMessagingServiceWorker,
} from '../services/fcmWebRegistration';
// ALERT_SOUND_DISABLED:
// import { primeNotificationAudio } from '../utils/notificationSound';
import { requiresPushUserGesture } from '../utils/webPushCapabilities';

/**
 * FCM web push: permission state, token registration, refresh, and lifecycle.
 */
export const usePushNotifications = ({
  enabled = true,
  sessionReady = false,
  authUid = null,
} = {}) => {
  const registerDeviceMutation = useRegisterNotificationDeviceMutation();
  const unregisterDeviceMutation = useUnregisterNotificationDeviceMutation();
  const registerDeviceRef = useRef(registerDeviceMutation.mutateAsync);
  const unregisterDeviceRef = useRef(unregisterDeviceMutation.mutateAsync);

  registerDeviceRef.current = registerDeviceMutation.mutateAsync;
  unregisterDeviceRef.current = unregisterDeviceMutation.mutateAsync;

  useEffect(() => {
    configureFcmDeviceApi({
      registerDevice: async (payload) => {
        try {
          return await registerDeviceRef.current(payload);
        } catch (error) {
          return {
            success: false,
            message: error?.message || 'Failed to register device for notifications',
          };
        }
      },
      unregisterDevice: async (payload) => {
        try {
          return await unregisterDeviceRef.current(payload);
        } catch (error) {
          return {
            success: false,
            message: error?.message || 'Failed to unregister device for notifications',
          };
        }
      },
    });

    return () => {
      resetFcmDeviceApi();
    };
  }, []);

  const [permission, setPermission] = useState(() => getNotificationPermission());
  const [blockReason, setBlockReason] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [availability, setAvailability] = useState({ available: false });

  const lastAuthUidRef = useRef(null);
  const effectiveAuthUid = authUid ?? auth.currentUser?.uid ?? null;

  const refreshStatus = useCallback(async () => {
    if (!enabled) {
      setAvailability({ available: false });
      setBlockReason(null);
      return;
    }

    const currentPermission = getNotificationPermission();
    setPermission(currentPermission);

    const avail = await getWebPushAvailability();
    setAvailability(avail);

    if (!avail.available) {
      setBlockReason(avail.reason ?? FCM_BLOCK_REASON.UNSUPPORTED_BROWSER);
      return;
    }

    if (currentPermission !== 'granted') {
      setBlockReason(currentPermission === 'denied' ? FCM_BLOCK_REASON.PERMISSION_DENIED : null);
      return;
    }

    if (isPushOptInRequired()) {
      setBlockReason(FCM_BLOCK_REASON.PUSH_OPT_IN_REQUIRED);
      return;
    }

    if (requiresPushUserGesture() && !getStoredFcmWebToken()) {
      void warmUpFcmMessagingServiceWorker();
      setBlockReason(FCM_BLOCK_REASON.USER_GESTURE_REQUIRED);
      return;
    }

    if (!sessionReady || !effectiveAuthUid) {
      return;
    }

    const hasToken = Boolean(getStoredFcmWebToken());

    // Only refresh an existing registration — new setup starts from user gesture (banner or header menu).
    if (hasToken && !isPushOptInRequired()) {
      const result = await setupFcmWebRegistration({ userGesture: false });
      setBlockReason(result.success ? null : (result.reason ?? null));
      return;
    }

    if (requiresPushUserGesture() && !hasToken) {
      setBlockReason(FCM_BLOCK_REASON.USER_GESTURE_REQUIRED);
      return;
    }

    setBlockReason(null);
  }, [enabled, sessionReady, effectiveAuthUid]);

  const enablePush = useCallback(
    async ({ forceRefresh = true, skipPermissionPrompt = false } = {}) => {
      if (!enabled) {
        return { success: false, skipped: true };
      }

      // ALERT_SOUND_DISABLED — primeNotificationAudio();

      setIsRegistering(true);
      try {
        const runEnable = async () => {
          const currentPermission = getNotificationPermission();
          if (currentPermission === 'default' && !skipPermissionPrompt) {
            const permissionResult = await enableWebPushNotifications({ forceRefresh });
            setPermission(getNotificationPermission());
            if (!permissionResult.success) {
              setBlockReason(
                permissionResult.permission === 'denied'
                  ? FCM_BLOCK_REASON.PERMISSION_DENIED
                  : (permissionResult.reason ?? null),
              );
              return permissionResult;
            }
            setBlockReason(null);
            return permissionResult;
          }

          const result = await setupFcmWebRegistration({
            userGesture: true,
            forceRefresh: requiresPushUserGesture() ? false : forceRefresh,
          });
          setPermission(getNotificationPermission());
          setBlockReason(result.success ? null : (result.reason ?? null));
          return result;
        };

        return await withFcmOperationTimeout(runEnable(), undefined, 'Enable push');
      } catch (error) {
        const timedOut = /timed out/i.test(error?.message || '');
        const result = {
          success: false,
          message: error?.message,
          reason: timedOut ? FCM_BLOCK_REASON.PUSH_SERVICE_UNAVAILABLE : null,
        };
        setBlockReason(result.reason);
        return result;
      } finally {
        setIsRegistering(false);
      }
    },
    [enabled],
  );

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    void refreshStatus();
  }, [enabled, refreshStatus]);

  useEffect(() => {
    if (!enabled || sessionReady) {
      return;
    }

    const currentPermission = getNotificationPermission();
    setPermission(currentPermission);

    if (currentPermission === 'granted' && isPushOptInRequired()) {
      setBlockReason(FCM_BLOCK_REASON.PUSH_OPT_IN_REQUIRED);
      return;
    }

    if (!getStoredFcmWebToken()) {
      setBlockReason(null);
    }
  }, [enabled, sessionReady]);

  useEffect(() => {
    if (!enabled || !sessionReady || !effectiveAuthUid) {
      lastAuthUidRef.current = null;
      return undefined;
    }

    lastAuthUidRef.current = effectiveAuthUid;

    let cancelled = false;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !cancelled) {
        void refreshFcmWebRegistrationIfNeeded();
      }
    };

    const onPageShow = (event) => {
      if (event.persisted && !cancelled) {
        void refreshStatus();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);

    const stopPermissionWatcher = startNotificationPermissionWatcher(() => {
      void handleWebPushPermissionRevoked();
      if (!cancelled) {
        setBlockReason(FCM_BLOCK_REASON.PUSH_OPT_IN_REQUIRED);
      }
    });

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
      stopPermissionWatcher();
    };
  }, [enabled, sessionReady, effectiveAuthUid, refreshStatus]);

  useEffect(() => {
    if (!enabled || typeof navigator === 'undefined' || !navigator.permissions?.query) {
      return undefined;
    }

    let permissionStatus;
    navigator.permissions
      .query({ name: 'notifications' })
      .then((status) => {
        permissionStatus = status;
        status.onchange = () => {
          setPermission(getNotificationPermission());
          void refreshStatus();
        };
      })
      .catch(() => undefined);

    return () => {
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, [enabled, refreshStatus]);

  const isConnected =
    permission === 'granted' &&
    Boolean(getStoredFcmWebToken()) &&
    !isPushOptInRequired() &&
    !blockReason;

  return {
    permission,
    blockReason,
    blockReasonMessage: blockReason ? getFcmBlockReasonMessage(blockReason) : null,
    isSupported: availability.available,
    isConnected,
    isRegistering,
    enablePush,
    refreshStatus,
    FCM_BLOCK_REASON,
  };
};

export default usePushNotifications;
